import React, { useEffect, useState } from "react";
import { ChevronDown, Play, ArrowRight, Star, Award, Globe, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import ArtistModal from "../components/ArtistModal";
import HomeGallery from "../components/HomeGallery";
import DancersBand from "../components/DancersBand";

const HomePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [artists, setArtists] = useState([]);
  const [modalArtist, setModalArtist] = useState(null);
  const [galleryItems, setGalleryItems] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/artists").then((r) => setArtists(r.data)).catch(() => {});
    api.get("/gallery").then((r) => setGalleryItems(r.data)).catch(() => {});
  }, []);

  const handleArtistClick = (artist) => {
    // Show modal preview for everyone — no forced login
    setModalArtist(artist);
  };

  const featuredArtists = artists.filter((a) => a.featured);
  const stats = [
    { icon: <Users size={24} />, value: "200+", label: "Artists Managed" },
    { icon: <Globe size={24} />, value: "45+", label: "Countries Reached" },
    { icon: <Award size={24} />, value: "150+", label: "Industry Awards" },
    { icon: <Star size={24} />, value: "12+", label: "Years of Excellence" },
  ];

  const styles = [
    "Urban", "Contemporary", "Afrofusion", "Bollywood", "Classical",
    "Breaking", "Hip-Hop", "Commercial", "Street", "Jazz",
    "Ballet", "Fusion", "Choreography", "Musical Theatre",
  ];

  return (
    <div className="bg-[#121212] text-white" data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: "url('/images/hero-bg.jpg')" }} />
        {/* Soft top-only gradient just enough for nav legibility — dancers stay fully visible */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-transparent to-transparent" style={{ height: "35%" }} />
        <div className="absolute top-0 left-0 right-0 h-px gold-gradient" />

        <div className="relative z-10 text-center px-6 max-w-6xl mx-auto">
          <div className="animate-fade-in-up flex items-center justify-center gap-3 mb-8">
            <div className="h-px w-12 bg-gold/60" />
            <span className="font-body text-gold text-[0.65rem] font-semibold tracking-[0.4em] uppercase">
              Premium Artist Management
            </span>
            <div className="h-px w-12 bg-gold/60" />
          </div>

          <h1 className="font-display animate-fade-in-up-delay-1 font-light mb-6" style={{ textShadow: "0 6px 40px rgba(0,0,0,0.85), 0 2px 8px rgba(0,0,0,0.6)" }}>
            <span className="block text-white" style={{ fontSize: "clamp(3.5rem, 10vw, 8rem)", lineHeight: 1, marginBottom: "0.25em" }}>
              Where Talent
            </span>
            <span className="block gold-shine italic" style={{ fontSize: "clamp(3.5rem, 10vw, 8rem)", lineHeight: 1.2, paddingBottom: "0.2em" }}>
              Meets Legacy
            </span>
          </h1>

          <div className="mb-16" />

          <div className="animate-fade-in-up-delay-3 flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate(isAuthenticated ? "/portal" : "/login")}
              className="btn-gold px-10 py-4 text-xs rounded-sm flex items-center gap-3 justify-center"
              data-testid="hero-view-roster"
            >
              <span>View Artist Roster</span>
              <ArrowRight size={14} />
            </button>
            <button
              onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })}
              className="btn-outline-gold px-10 py-4 text-xs rounded-sm flex items-center gap-3 justify-center"
              data-testid="hero-our-story"
            >
              <Play size={14} />
              <span>Our Story</span>
            </button>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <span className="font-body text-white/30 text-[0.6rem] tracking-[0.3em] uppercase">Scroll</span>
          <ChevronDown size={16} className="text-gold/60" />
        </div>
      </section>

      {/* STATS */}
      <section className="py-16 border-y border-gold/10">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="flex flex-col items-center text-center gap-3">
              <div className="text-gold/40">{s.icon}</div>
              <div className="stat-number text-4xl font-bold">{s.value}</div>
              <div className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section id="artists" className="py-28 max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-8 bg-gold/60" />
              <span className="font-body text-gold text-[0.65rem] font-semibold tracking-[0.4em] uppercase">
                Featured Talent
              </span>
            </div>
            <h2 className="font-display font-light text-5xl md:text-6xl text-white leading-tight">Our Artists</h2>
          </div>
          <button
            onClick={() => navigate(isAuthenticated ? "/portal" : "/login")}
            className="btn-outline-gold text-xs px-6 py-3 rounded-sm flex items-center gap-2 w-fit"
            data-testid="home-view-full-roster"
          >
            <span>View Full Roster</span>
            <ArrowRight size={13} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuredArtists.map((artist) => (
            <div
              key={artist.id}
              className="artist-card relative overflow-hidden rounded-sm cursor-pointer group"
              style={{ aspectRatio: "3/4" }}
              onClick={() => handleArtistClick(artist)}
              data-testid={`featured-artist-${artist.id}`}
            >
              <img
                src={artist.image}
                alt={artist.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                style={{ minHeight: "420px" }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
              <div className="card-overlay absolute inset-0 bg-black/40 border border-gold/30" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {artist.specialties.slice(0, 2).map((spec) => (
                    <span key={spec} className="specialty-tag">{spec}</span>
                  ))}
                </div>
                <h3 className="font-display text-2xl font-light text-white mb-1">{artist.name}</h3>
                <p className="font-body text-white/50 text-xs tracking-wider uppercase mb-4">{artist.title}</p>
                <div className="card-overlay flex items-center gap-2 text-gold text-xs font-semibold tracking-widest uppercase">
                  <span>View Profile</span>
                  <ArrowRight size={12} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SUBTLE PANORAMIC BAND — drifts slowly between sections */}
      <DancersBand />

      {/* ABOUT */}
      <section id="about" className="py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent" />
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-8 bg-gold/60" />
              <span className="font-body text-gold text-[0.65rem] font-semibold tracking-[0.4em] uppercase">Our Vision</span>
            </div>
            <h2 className="font-display font-light text-4xl md:text-5xl text-white leading-tight mb-3">
              MRVA: Redefining
            </h2>
            <h2 className="font-display font-light text-4xl md:text-5xl mb-8" style={{ lineHeight: 1.15 }}>
              <span className="gold-gradient-text italic" style={{ paddingBottom: "0.1em", display: "inline-block" }}>
                Kuwait's Creative Landscape
              </span>
            </h2>

            <p className="font-body text-white/55 text-sm leading-relaxed mb-5">
              Based in <span className="text-gold">Kuwait City</span>, MRVA is a multi-disciplinary powerhouse disrupting the status quo in Production, Talent Management, and Events. We operate at the intersection of local heritage and global innovation, delivering projects that are as culturally resonant as they are technically advanced.
            </p>
            <p className="font-body text-white/55 text-sm leading-relaxed mb-10">
              At MRVA, we don't just represent talent — we <span className="text-white">architect careers</span>. Through our global network across film, television, fashion, and live entertainment, we forge strategic partnerships that elevate visionary artists into industry leaders.
            </p>

            {/* Core Pillars */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-5">
                <div className="h-px w-6 bg-gold/60" />
                <span className="font-body text-gold text-[0.6rem] font-semibold tracking-[0.4em] uppercase">Our Core Pillars</span>
              </div>
              <div className="space-y-4">
                {[
                  {
                    no: "01",
                    title: "Production",
                    body: "Delivering cutting-edge creative content and high-impact live experiences.",
                  },
                  {
                    no: "02",
                    title: "Talent Management",
                    body: "Curating a handpicked roster of visionaries and building platforms for lasting influence.",
                  },
                  {
                    no: "03",
                    title: "Global Reach",
                    body: "Connecting local culture with international opportunities through world-class brand collaborations.",
                  },
                ].map((p) => (
                  <div
                    key={p.no}
                    className="group flex gap-5 items-start py-3 border-l border-gold/15 hover:border-gold/60 pl-5 transition-all"
                  >
                    <span className="font-heading text-gold/60 text-sm font-semibold tracking-widest pt-0.5 group-hover:text-gold transition-colors">
                      {p.no}
                    </span>
                    <div className="flex-1">
                      <h3 className="font-display text-xl text-white mb-1">{p.title}</h3>
                      <p className="font-body text-white/45 text-sm leading-relaxed">{p.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Closing tagline */}
            <p className="font-display italic text-white/80 text-lg md:text-xl leading-snug mb-10 border-l-2 border-gold/60 pl-5">
              MRVA doesn't just participate in the industry — <span className="gold-gradient-text" style={{ display: "inline-block", paddingBottom: "0.05em" }}>we define its next movement.</span>
            </p>

            <button
              onClick={() => navigate("/contact")}
              className="btn-gold px-8 py-3.5 text-xs rounded-sm flex items-center gap-3 w-fit"
            >
              <span>Work With Us</span>
              <ArrowRight size={13} />
            </button>
          </div>

          <div className="relative">
            {/* Soft copper radial glow behind the illustration so it lifts off the dark bg */}
            <div
              className="absolute inset-0 -z-10"
              style={{
                background:
                  "radial-gradient(ellipse at 50% 45%, rgba(184,115,51,0.18) 0%, rgba(184,115,51,0.08) 35%, transparent 70%)",
                filter: "blur(40px)",
              }}
            />

            {/* The illustration — transparent PNG, floats gently */}
            <div
              className="relative flex items-center justify-center"
              style={{ animation: "floatY 8s ease-in-out infinite" }}
            >
              <img
                src="/images/vision-artists.png"
                alt="MRVA collective — every artist, every discipline, one stage"
                className="w-full h-auto max-w-[640px] mx-auto block"
                style={{
                  filter: "drop-shadow(0 30px 50px rgba(0,0,0,0.5)) drop-shadow(0 0 30px rgba(184,115,51,0.15))",
                }}
              />
            </div>

            {/* Decorative copper frames */}
            <div className="absolute -bottom-5 -right-5 w-36 h-36 border border-gold/25 rounded-sm -z-10" />
            <div className="absolute -top-5 -left-5 w-24 h-24 border border-gold/15 rounded-sm -z-10" />

            {/* Floating accent label */}
            <div className="absolute -bottom-5 left-6 hidden md:flex items-center gap-3 px-5 py-2.5 bg-black/85 backdrop-blur-md rounded-sm shadow-2xl">
              <div className="h-px w-6 bg-gold" />
              <p className="font-display text-white text-base leading-none tracking-wide">
                Years of <span className="gold-gradient-text italic">Excellence</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-6 border-t border-b border-gold/10 overflow-hidden">
        <div className="flex" style={{ animation: "marquee 30s linear infinite" }}>
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center gap-8 flex-shrink-0 pr-8">
              {styles.map((s) => (
                <React.Fragment key={s}>
                  <span className="font-heading text-white/15 text-sm font-semibold tracking-[0.25em] uppercase whitespace-nowrap">{s}</span>
                  <span className="text-gold/30 text-xs">✦</span>
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* SPECIALTIES */}
      <section className="py-20 border-t border-gold/10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-8 bg-gold/60" />
              <span className="font-body text-gold text-[0.65rem] font-semibold tracking-[0.4em] uppercase">Disciplines</span>
              <div className="h-px w-8 bg-gold/60" />
            </div>
            <h2 className="font-display font-light text-4xl text-white">Every Style. Every Stage.</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {styles.map((s) => (
              <span key={s} className="specialty-tag cursor-default text-sm py-2 px-5">{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 border-t border-gold/10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-8 bg-gold/60" />
              <span className="font-body text-gold text-[0.65rem] font-semibold tracking-[0.4em] uppercase">Testimonials</span>
              <div className="h-px w-8 bg-gold/60" />
            </div>
            <h2 className="font-display font-light text-4xl md:text-5xl text-white">What Clients Say</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { quote: "MRVA didn't just provide us with dancers — they delivered a vision. Every artist was perfectly aligned with our brand's DNA.", name: "Alicia Chen", role: "Creative Director, Nike EMEA" },
              { quote: "Working with MRVA is like having a Michelin-star experience in talent management. The artists are exceptional, the service impeccable.", name: "Marcus Webb", role: "Executive Producer, BBC Arts" },
              { quote: "The artists MRVA represents have a rare quality — technical brilliance and authentic stage presence. Completely transformed our production.", name: "Sofia Russo", role: "Artistic Director, London Fashion Week" },
            ].map((t) => (
              <div key={t.name} className="glass rounded-sm p-8 flex flex-col">
                <div className="text-gold text-3xl font-display mb-4">“</div>
                <p className="font-body text-white/50 text-sm leading-relaxed flex-1 mb-6 italic">{t.quote}</p>
                <div className="gold-divider mb-5" />
                <div>
                  <div className="font-heading text-sm font-semibold text-white tracking-wider">{t.name}</div>
                  <div className="font-body text-gold/60 text-[0.65rem] tracking-wider uppercase mt-0.5">{t.role}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 pt-12 border-t border-gold/10">
            <p className="font-body text-white/20 text-[0.65rem] tracking-[0.4em] uppercase text-center mb-8">Trusted by global brands</p>
            <div className="flex flex-wrap justify-center gap-x-12 gap-y-4">
              {["Nike", "BBC", "Channel 4", "Vogue", "Louis Vuitton", "Sony Music", "Netflix"].map((b) => (
                <span key={b} className="font-heading text-white/15 text-sm font-bold tracking-widest uppercase hover:text-white/30 transition-colors cursor-default">{b}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY (dynamic — populated by admin) */}
      <HomeGallery items={galleryItems} />

      {/* CTA */}
      <section className="py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/images/hero-bg.jpg')" }} />
        <div className="absolute inset-0 bg-black/80" />
        <div className="absolute top-0 left-0 right-0 h-px gold-gradient" />
        <div className="absolute bottom-0 left-0 right-0 h-px gold-gradient" />
        <div className="relative z-10 text-center px-6 max-w-3xl mx-auto">
          <h2 className="font-display font-light text-5xl md:text-6xl text-white mb-6 leading-tight">
            Ready to Discover<br />
            <span className="gold-shine italic">Exceptional Talent?</span>
          </h2>
          <p className="font-body text-white/50 text-sm mb-10 max-w-lg mx-auto leading-relaxed">
            Access our full artist roster, booking information, and exclusive showcases through our private client portal.
          </p>
          <button
            onClick={() => navigate(isAuthenticated ? "/portal" : "/login")}
            className="btn-gold px-12 py-4 text-xs rounded-sm inline-flex items-center gap-3"
          >
            <span>Enter Client Portal</span>
            <ArrowRight size={14} />
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 border-t border-gold/10">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img
              src="/images/mrva-logo-white.png"
              alt="MRVA Productions"
              className="h-12 w-auto object-contain"
            />
          </div>
          <p className="font-body text-white/25 text-[0.65rem] tracking-widest uppercase text-center">
            © 2026 MRVA Productions. All rights reserved.
          </p>
          <div className="flex gap-6">
            {["Instagram", "Twitter", "YouTube"].map((s) => (
              <a key={s} href="#" className="font-body text-white/30 text-[0.65rem] tracking-widest uppercase hover:text-gold transition-colors">{s}</a>
            ))}
          </div>
        </div>
      </footer>
      {modalArtist && (
        <ArtistModal artist={modalArtist} onClose={() => setModalArtist(null)} />
      )}
    </div>
  );
};

export default HomePage;
